import {
	DocConflictError,
	DocConvertedError,
	DocDeletedError,
	DocLimitError,
	DocStoreUnavailableError,
	ProRequiredError,
	RemoteUnreachableError,
	type DocMeta,
	type DocStore,
	type DocSyncMode,
	type StoredDocEnvelope,
	type SyncMeta,
	type SyncRevisionStore,
	type YDocPersistence,
} from './doc-store';
import type { RemoteDocClient, RemoteDocMeta } from './remote-doc-store';
import { reconcileDocLists } from './sync-reconcile';

export type SyncedDocStoreOptions<TDoc extends StoredDocEnvelope, TMetaExtra> = {
	local: DocStore<TDoc, TMetaExtra> & SyncRevisionStore;
	remote: RemoteDocClient<TDoc, TMetaExtra>;
	prepare?: () => Promise<void>;
	/** Builds the meta-extra slice (e.g. a diagram's `kind`) from a remote
	 *  list entry — used by list()'s reconcile. */
	metaExtraFromRemote: (r: RemoteDocMeta<TMetaExtra>) => TMetaExtra;
	/** Clock for the GET handoff below (test seam). */
	now?: () => number;
};

/** How long a GET made by getRemoteSyncMode may stand in for the next
 *  loadWithRevision of the same id (they run back to back when an editor
 *  opens: mode resolution, then the session's load). */
const REMOTE_GET_HANDOFF_MS = 5000;

/** Write-through DocStore: local IndexedDB is always the fast path and
 *  the source of truth when offline, remote is synced alongside it. Reads
 *  prefer remote (freshest across devices) but fall back to local whenever
 *  the remote is unreachable; writes land locally first so a flaky network
 *  never loses data. `list()` also drives two best-effort background
 *  passes — uploading local-only (phase-1) docs and backfilling thumbnails
 *  the local store hasn't cached yet — neither of which blocks the call
 *  that triggered them.
 *
 *  An optional `prepare` hook runs once, at construction, and every method
 *  below awaits it before doing anything else — this is how a caller (e.g.
 *  the account-switch ownership check in store-instance.ts) gets to run
 *  async reconciliation (potentially wiping `local`) before any op is
 *  allowed to read or write. */
export class SyncedDocStore<TDoc extends StoredDocEnvelope, TMetaExtra>
implements DocStore<TDoc, TMetaExtra> {
	private readonly local: DocStore<TDoc, TMetaExtra> & SyncRevisionStore;
	private readonly remote: RemoteDocClient<TDoc, TMetaExtra>;
	private readonly metaExtraFromRemote: (r: RemoteDocMeta<TMetaExtra>) => TMetaExtra;
	private readonly now: () => number;
	/** Single-use: the last successful GET made by getRemoteSyncMode. Dropped
	 *  by any write to that id, by use, and after REMOTE_GET_HANDOFF_MS. */
	private handoff: { id: string; at: number; result: Awaited<ReturnType<RemoteDocClient<TDoc, TMetaExtra>['get']>> } | null = null;
	/** Resolves once `opts.prepare()` (if any) has settled — awaited at the
	 *  top of every method below so no read/write can race ahead of
	 *  account-switch reconciliation (see store-instance.ts's ownership
	 *  `prepare` hook). A rejection here propagates to whichever call
	 *  happens to run first; callers must not treat that as "no-op" —
	 *  see the class doc. */
	private readonly ready: Promise<void>;
	private _lastListOffline = false;
	/** Listeners notified when something a prior list() rendered has changed
	 *  underneath it (backfilled thumbnails, a device-only document that
	 *  uploaded, an archive). A gallery subscribes to re-list() — see
	 *  subscribeBackgroundSync. */
	private readonly backgroundSyncListeners = new Set<() => void>();

	constructor(opts: SyncedDocStoreOptions<TDoc, TMetaExtra>) {
		this.local = opts.local;
		this.remote = opts.remote;
		this.metaExtraFromRemote = opts.metaExtraFromRemote;
		this.now = opts.now ?? Date.now;
		const ready = opts.prepare ? opts.prepare() : Promise.resolve();
		// Attach a no-op catch on a side reference so an unawaited rejection
		// (nothing has called an op yet) doesn't surface as an unhandled
		// promise rejection — the REAL handling happens wherever `this.ready`
		// itself is awaited, in each method below.
		ready.catch(() => {});
		this.ready = ready;
	}

	/** Set by each list() call — true when the last list() had to fall back
	 *  to the local store because the remote was unreachable. */
	get lastListOffline(): boolean {
		return this._lastListOffline;
	}

	/** Subscribe to changes that make an earlier list() stale: background passes
	 *  that cached NEW local data the triggering list() rendered without
	 *  (thumbnails backfilled from the remote, see backfillThumbnails), a
	 *  device-only document whose upload landed (see uploadLocalOnly), and an
	 *  archive or unarchive made through this store (SyncedDiagramStore), which
	 *  moves a document between the active and archived lists. The gallery
	 *  re-list()s on this signal — every gallery, including ones that did not
	 *  make the change (the limit dialog archives on its own). Returns an
	 *  unsubscribe fn. */
	subscribeBackgroundSync(listener: () => void): () => void {
		this.backgroundSyncListeners.add(listener);
		return () => {
			this.backgroundSyncListeners.delete(listener);
		};
	}

	/** Only for a change list() would now report differently: each listener
	 *  re-lists, so firing on a no-op would spin list → notify → list. */
	protected notifyBackgroundSync(): void {
		for (const listener of this.backgroundSyncListeners) listener();
	}

	/** Last revision recorded for `id` by save()/load() — what the room client
	 *  reports over the socket after each save. Null for never-synced docs. */
	async getRecordedRevision(id: string): Promise<number | null> {
		await this.ready;
		return this.local.getRevision(id);
	}

	/** The local store's Yjs persistence, when it has one (azabu-diagrams v3
	 *  does; azabu-patterns has the methods but no store behind them). */
	private yLocal(): YDocPersistence | null {
		const l = this.local as Partial<YDocPersistence>;
		return l.hasYDocs === true ? (l as YDocPersistence) : null;
	}

	async loadYDoc(id: string): Promise<Uint8Array | null> {
		await this.ready;
		return (await this.yLocal()?.loadYDoc(id)) ?? null;
	}

	async saveYDoc(id: string, update: Uint8Array): Promise<void> {
		await this.ready;
		const y = this.yLocal();
		if (y == null) throw new Error('local store has no Yjs persistence');
		await y.saveYDoc(id, update);
	}

	async deleteYDoc(id: string): Promise<void> {
		await this.ready;
		await this.yLocal()?.deleteYDoc(id);
	}

	/** What the server says about this id right now (SP-52 mode resolution).
	 *  'archived' wins over the sync mode: an archived document is frozen
	 *  whichever way it would otherwise sync. */
	async getRemoteSyncMode(id: string): Promise<DocSyncMode | 'archived' | 'missing' | 'deleted' | 'unreachable'> {
		await this.ready;
		try {
			const r = await this.remote.get(id);
			if (r != null) this.handoff = { id, at: this.now(), result: r };
			if (r == null) return 'missing';
			if (r.archived === true) return 'archived';
			return r.sync ?? 'legacy';
		} catch (err) {
			if (err instanceof DocDeletedError) return 'deleted';
			if (err instanceof RemoteUnreachableError) return 'unreachable';
			throw err;
		}
	}

	/** Uploads unsynced local edits BEFORE a diagram switches to Yjs, so the
	 *  seed is built from the latest JSON. 'conflict' means the server moved on
	 *  without these edits (another device saved or converted it) — the caller
	 *  must offer keep-as-copy, because loading remote-first would overwrite
	 *  them. `name` is the local copy's name, for that dialog. */
	async pushIfDirty(id: string): Promise<{ status: 'clean' | 'pushed' | 'conflict' | 'offline'; name: string | null }> {
		await this.ready;
		const doc = await this.local.load(id);
		const meta = await this.local.getSyncMeta(id);
		const name = doc?.name ?? null;
		if (doc == null || meta == null || !this.isDirty(doc.updatedAt, meta)) return { status: 'clean', name };
		this.dropHandoff(id);
		try {
			const revision = await this.remote.put(doc, meta.revision);
			await this.local.setRevision(id, revision, doc.updatedAt);
			return { status: 'pushed', name };
		} catch (err) {
			if (err instanceof DocConflictError) return { status: 'conflict', name };
			if (err instanceof RemoteUnreachableError) return { status: 'offline', name };
			throw err;
		}
	}

	/** Refreshes the LOCAL copy of a Yjs-synced document from its projection
	 *  (never PUTs), keeping it marked clean so list()/tombstone logic treat it
	 *  as an already-synced cache. */
	async saveLocalClean(doc: TDoc): Promise<void> {
		await this.ready;
		await this.local.save(doc);
		const revision = await this.local.getRevision(doc.id);
		if (revision != null) await this.local.setRevision(doc.id, revision, doc.updatedAt);
	}

	async save(doc: TDoc): Promise<void> {
		await this.write(doc, {});
	}

	/** Like save(), but asks the server to store the document already archived,
	 *  so it takes no active slot. This is how work is brought in (import, a
	 *  claimed `/try` draft, keep-as-copy) when the account has no free slot: the
	 *  device copy lands first, exactly as in save(), and the document is never
	 *  dropped for want of room. */
	async saveArchived(doc: TDoc): Promise<void> {
		await this.write(doc, { archive: true });
	}

	private async write(doc: TDoc, opts: { archive?: boolean }): Promise<void> {
		await this.ready;
		// A Yjs-synced document never takes the whole-document PUT path (SP-52).
		if ((await this.yLocal()?.loadYDoc(doc.id)) != null) throw new DocConvertedError(null);
		this.dropHandoff(doc.id);
		await this.local.save(doc);
		const baseRevision = await this.local.getRevision(doc.id);
		const newRevision = await this.remote.put(doc, baseRevision, opts);
		await this.local.setRevision(doc.id, newRevision, doc.updatedAt);
	}

	/** Like load(), but also returns the revision this load OBSERVED — used to
	 *  seed the session's per-tab lastKnownRevision without a second (shared,
	 *  race-prone) getRecordedRevision read. */
	async loadWithRevision(id: string): Promise<{ doc: TDoc; revision: number | null; sync: DocSyncMode | null } | null> {
		await this.ready;
		try {
			const remoteResult = this.takeHandoff(id) ?? (await this.remote.get(id));
			if (remoteResult == null) {
				// 404. If we recorded a revision, this doc WAS on the server and is
				// now gone — a delete from before tombstones shipped, which left no
				// tombstone row. Treat it as one. (This replaces the old behavior of
				// clearing the revision and serving local, which re-created the doc
				// on the next autosave AND destroyed the evidence list() needs.)
				if ((await this.local.getSyncMeta(id)) != null) return await this.resolveTombstone(id);
				// No revision: never synced. A phase-1 local doc — serve it.
				const doc = await this.local.load(id);
				return doc == null ? null : { doc, revision: null, sync: null };
			}
			// Offline-edit protection: if the remote revision matches what we last
			// recorded, the server has nothing we haven't already synced. Do NOT
			// cache-write here — an offline save may have written a NEWER local doc
			// whose remote.put failed (so the recorded revision never advanced);
			// overwriting local with the (older-or-equal) remote copy would silently
			// lose that edit. Only fall back to the remote doc if local is missing.
			let doc: TDoc | null = null;
			const localRevision = await this.local.getRevision(id);
			if (localRevision === remoteResult.revision) doc = await this.local.load(id);
			if (doc == null) {
				await this.local.save(remoteResult.doc);
				await this.local.setRevision(id, remoteResult.revision, remoteResult.doc.updatedAt);
				doc = remoteResult.doc;
			}
			return { doc, revision: remoteResult.revision, sync: remoteResult.sync ?? 'legacy' };
		} catch (err) {
			if (err instanceof DocDeletedError) return await this.resolveTombstone(id);
			if (!(err instanceof RemoteUnreachableError)) throw err;
			const doc = await this.local.load(id);
			return doc == null ? null : { doc, revision: await this.local.getRevision(id), sync: null };
		}
	}

	/** The server says this id is gone. Only purge when there is POSITIVE evidence
	 *  the local copy is a clean, already-synced cache (`meta != null` and not
	 *  dirty) — then it's safe to drop and report the document missing. Anything
	 *  else — dirty, or no syncMeta at all — throws with the doc attached and lets
	 *  the caller offer keep-as-copy; purging it would be silent data loss.
	 *
	 *  `meta == null` means we have no evidence this copy was EVER synced, i.e. it
	 *  is 100% unsynced work — the case MOST deserving of protection, not least.
	 *  Treating "no syncMeta" as "safe to purge" (the previous, inverted behavior)
	 *  would silently destroy exactly the work this feature exists to save. */
	private async resolveTombstone(id: string): Promise<null> {
		const doc = await this.local.load(id);
		const meta = await this.local.getSyncMeta(id);
		if (doc != null && (meta == null || this.isDirty(doc.updatedAt, meta))) {
			throw new DocDeletedError(null, doc);
		}
		await this.purgeLocal(id);
		return null;
	}

	async load(id: string): Promise<TDoc | null> {
		return (await this.loadWithRevision(id))?.doc ?? null;
	}

	/** load() for a read-only view (a diagram's view page, an embed). It never
	 *  writes over device work: when the local copy holds unsynced edits — dirty,
	 *  or with no sync record at all (by the SyncMeta convention, never-synced
	 *  work) — it is served as it is and left untouched, even when the server has
	 *  a newer revision (load() adopts that one over it). Nothing reconciles from a
	 *  view: an archived diagram refuses the push anyway (423), and the editor's
	 *  own load is where an active one reconciles. A clean or missing local copy
	 *  loads exactly as load() does, and so does one that no longer parses: there
	 *  are no readable edits in it to protect, and load() can heal it from the
	 *  server's newer copy. */
	async loadForView(id: string): Promise<TDoc | null> {
		await this.ready;
		let local: TDoc | null;
		try {
			local = await this.local.load(id);
		} catch (err) {
			if (err instanceof DocStoreUnavailableError) throw err;
			local = null;
		}
		if (local != null) {
			const meta = await this.local.getSyncMeta(id);
			if (meta == null || this.isDirty(local.updatedAt, meta)) {
				// A GET that getRemoteSyncMode left for this load is not needed.
				this.dropHandoff(id);
				return local;
			}
		}
		return this.load(id);
	}

	async list(): Promise<DocMeta<TMetaExtra>[]> {
		await this.ready;
		let remoteMetas: RemoteDocMeta<TMetaExtra>[];
		try {
			remoteMetas = await this.remote.list();
		} catch (err) {
			if (err instanceof RemoteUnreachableError) {
				this._lastListOffline = true;
				return this.local.list();
			}
			throw err;
		}
		this._lastListOffline = false;

		const localMetas = await this.local.list();
		// The whole thumbnail cache, keyed by id — sourced separately from
		// localMetas so a remote-only doc (no local document record on this
		// device) still gets the preview backfillThumbnails cached for it.
		const thumbById = await this.local.getThumbnails();
		const { metas, localOnly } = reconcileDocLists(remoteMetas, localMetas, this.metaExtraFromRemote, thumbById);
		// Awaited, unlike the background passes below: it decides what the caller
		// RENDERS. Costs nothing in the common case — there are no local-only ids.
		const { toUpload, visible } = await this.resolveLocalOnly(localOnly, metas);

		void this.uploadLocalOnly(toUpload);
		void this.backfillThumbnails(remoteMetas, thumbById);

		return visible;
	}

	/** Decides what each local-only id actually IS. The server hard-deleted rows
	 *  until tombstones shipped, so "absent from the list" alone is ambiguous —
	 *  it means either "never uploaded" or "deleted on another device", and
	 *  guessing wrong the second way re-uploads a document the user deleted.
	 *
	 *  The recorded revision disambiguates: it means the doc WAS on the server.
	 *  We then probe to find out what the server says now:
	 *    410 → tombstoned.
	 *    404 → also gone. Every document deleted BEFORE tombstones shipped has
	 *          no tombstone row, so without this the fix covers only new deletes.
	 *    200 → alive; our list snapshot was merely stale (a concurrent create).
	 *          Skipping the probe here would delete a doc the user just made.
	 *  Unreachable → decide nothing. Never delete local data on the word of a
	 *          server we could not reach.
	 *
	 *  The ids with no recorded revision (`toUpload`) come back flagged
	 *  `localOnly` in `visible`: the server has never seen them. */
	private async resolveLocalOnly(
		localOnly: string[],
		metas: DocMeta<TMetaExtra>[],
	): Promise<{ toUpload: string[]; visible: DocMeta<TMetaExtra>[] }> {
		const toUpload: string[] = [];
		const purged = new Set<string>();
		const flagged = new Set<string>();

		for (const id of localOnly) {
			const meta = await this.local.getSyncMeta(id);
			if (meta == null) {
				toUpload.push(id); // never uploaded — a phase-1 doc. No probe.
				continue;
			}
			let tombstoned: boolean;
			try {
				tombstoned = (await this.remote.get(id)) == null;
			} catch (err) {
				if (err instanceof DocDeletedError) tombstoned = true;
				else if (err instanceof RemoteUnreachableError) continue;
				else {
					// A single unparseable/unreachable-in-a-weird-way document must not
					// blank the whole library (list() has no other catch above this to
					// stop the throw from propagating out to useDocList.refresh, whose
					// catch reads ANY failure as "load failed" and empties the gallery).
					// Leave this one doc alone and retry it next visit, exactly like the
					// RemoteUnreachableError branch above.
					console.debug('SyncedDocStore: probe failed for local-only doc, will retry next visit', id, err);
					continue;
				}
			}
			if (!tombstoned) continue;

			const listed = metas.find((m) => m.id === id);
			const dirty = listed != null && this.isDirty(listed.updatedAt, meta);
			if (dirty) {
				// Real work that exists only on this device. Keep it and let the
				// gallery offer keep-as-copy — purging here would be silent loss.
				flagged.add(id);
			} else {
				// Just a stale cache of a document the user deleted. Drop it.
				await this.purgeLocal(id);
				purged.add(id);
			}
		}

		const uploading = new Set(toUpload);
		const visible = metas
			.filter((m) => !purged.has(m.id))
			.map((m) => {
				if (flagged.has(m.id)) return { ...m, deletedElsewhere: true };
				// The server has never seen it: it exists on this device only until
				// the upload below lands (and the server may refuse it).
				if (uploading.has(m.id)) return { ...m, localOnly: true };
				return m;
			});
		return { toUpload, visible };
	}

	/** Drops every local trace of a document WITHOUT touching the remote —
	 *  `this.local.delete` clears the doc, its thumbnail, and its syncMeta in one
	 *  transaction. Deliberately not `this.delete()`, whose remote leg would be a
	 *  pointless DELETE against an already-tombstoned id. */
	private async purgeLocal(id: string): Promise<void> {
		this.dropHandoff(id);
		await this.local.delete(id);
	}

	private takeHandoff(id: string): Awaited<ReturnType<RemoteDocClient<TDoc, TMetaExtra>['get']>> {
		const h = this.handoff;
		if (h == null || h.id !== id) return null;
		this.handoff = null;
		return this.now() - h.at <= REMOTE_GET_HANDOFF_MS ? h.result : null;
	}

	private dropHandoff(id: string): void {
		if (this.handoff?.id === id) this.handoff = null;
	}

	/** True when the local copy holds edits that were never synced.
	 *
	 *  A null `syncedUpdatedAt` means the record predates the field existing, and
	 *  by contract (see SyncMeta) those read as CLEAN. That guard is load bearing:
	 *  without it EVERY pre-upgrade record — i.e. the entire installed base this
	 *  fix exists for — compares `number !== null`, reads as dirty, and gets
	 *  flagged "deleted elsewhere" instead of quietly purged. */
	private isDirty(localUpdatedAt: number, meta: SyncMeta): boolean {
		return meta.syncedUpdatedAt != null && localUpdatedAt !== meta.syncedUpdatedAt;
	}

	/** Rescues the local copy of a document that was deleted elsewhere, by saving
	 *  it under a FRESH id. It is never an undelete: the original id is tombstoned
	 *  permanently and its PUT would 410, so a new id is the only thing that can
	 *  work — and it needs no backend special case, being an ordinary first insert.
	 *
	 *  The name carries over unchanged: the original is gone, so there is nothing
	 *  live to disambiguate from and a "(copy)" suffix would only raise the
	 *  question "copy of what?". `createdAt` carries over too — the work predates
	 *  the copy, and resetting it would misrepresent the doc's age in a gallery
	 *  sorted by time.
	 *
	 *  Order matters: the copy lands BEFORE the original is dropped, so a failure
	 *  can never lose both. Returns the new id. */
	async keepAsCopy(id: string): Promise<string> {
		await this.ready;
		const doc = await this.local.load(id);
		if (doc == null) throw new Error(`keepAsCopy: no local document ${id}`);
		const newId = crypto.randomUUID();
		const copy = { ...doc, id: newId, updatedAt: Date.now() } as TDoc;
		// True when the copy had to be brought in archived (no free active slot).
		let archived = false;
		try {
			try {
				await this.save(copy);
			} catch (err) {
				// At the plan's active limit the server refuses a new ACTIVE document
				// (402). save() is local-first, so the copy is on disk already; the
				// server only said it has no slot for it. Land it archived instead —
				// the work reaches the account without taking a slot, and falling
				// through to the purge below is then as safe as on the unlimited path.
				if (!(err instanceof DocLimitError)) throw err;
				await this.saveArchived(copy);
				archived = true;
			}
		} catch (err) {
			// The copy is safe iff the LOCAL leg landed — so ask the store, don't
			// infer it from the error class. save() is local-first, and remote.put
			// throws DocTooLargeError (a client-side size check, before any network
			// call), a bare Error on any non-2xx, and RemoteUnreachableError all
			// with the local write already committed. Discriminating on
			// RemoteUnreachableError alone would rethrow past the purge below and
			// leave the copy AND the original on disk — a silent duplicate, and one
			// that retries into a new UUID every click. That is not hypothetical:
			// a doc that outgrew maxPutBodyBytes through offline edits is dirty
			// BECAUSE its autosave was failing, so size and dirtiness correlate —
			// it is this feature's own scenario.
			if ((await this.local.load(newId)) == null) throw err;
		}
		// this.saveThumbnail (not this.local.saveThumbnail) — the copy was never
		// opened in an editor, so autosave never captures a thumbnail for it, and
		// backfillThumbnails can't help since the remote reports hasThumbnail:
		// false for a doc it's never seen. Writing local-only would leave the card
		// blank on every OTHER device, permanently. saveThumbnail writes local then
		// fires the remote leg best-effort (already swallows failures).
		// The exception is an archived copy: the server refuses a thumbnail for a
		// frozen document (423), so that request could only fail — keep the preview
		// on this device and skip it.
		const thumb = (await this.local.list()).find((m) => m.id === id)?.thumbnail;
		if (thumb != null) {
			if (archived) await this.local.saveThumbnail(newId, thumb);
			else await this.saveThumbnail(newId, thumb);
		}
		await this.purgeLocal(id);
		return newId;
	}

	/** Throws away the local copy of a document deleted elsewhere. Local-only by
	 *  design — the remote already has its tombstone, and DELETEing a tombstoned
	 *  id would be a pointless round trip. */
	async discardDeleted(id: string): Promise<void> {
		await this.ready;
		await this.purgeLocal(id);
	}

	async delete(id: string): Promise<void> {
		await this.ready;
		this.dropHandoff(id);
		await this.local.delete(id);
		await this.local.deleteRevision(id);
		await this.remote.delete(id);
	}

	async saveThumbnail(id: string, blob: Blob): Promise<void> {
		await this.ready;
		await this.local.saveThumbnail(id, blob);
		// The autosave controller runs this in the background. Keep its
		// promise pending through the upload so it can serialize previews.
		await this.remote.putThumbnail(id, blob).catch(() => {});
	}

	/** Uploads phase-1 docs the remote has never seen. A conflict means
	 *  another client got there first — the doc resolves remote-first on
	 *  the next open, so it's skipped silently. Any other error is
	 *  swallowed to console.debug and simply retried on the next list().
	 *
	 *  Race note: this fires from list() without blocking it, so a
	 *  foreground save() of the same unmigrated doc can interleave with
	 *  this background put(null) — a tiny window (this runs once per
	 *  gallery visit, and a real edit-then-save takes longer than a
	 *  microtask tick). A full-reload navigation between the gallery and
	 *  the editors (see editor-switch-gpu-crash) tears down this in-flight
	 *  void-fired pass entirely before it could race a save on the new
	 *  page. If it ever does lose the race, the loser gets a 409; the
	 *  load-path revision guard above (treating a 404-with-recorded-revision
	 *  as a tombstone, and refusing to clobber on an equal revision) defuses
	 *  the reload-after-409 case so the retry that follows is a clean
	 *  re-sync rather than a clobber. */
	private async uploadLocalOnly(ids: string[]): Promise<void> {
		// Re-listing turns each uploaded row from `localOnly` into an ordinary one.
		// Nothing uploaded → nothing to say, which is what ends the re-list chain:
		// the re-list's own pass finds these ids recorded and uploads nothing.
		let uploadedAny = false;
		for (const id of ids) {
			try {
				const doc = await this.local.load(id);
				if (doc == null) continue;
				const revision = await this.remote.put(doc, null);
				await this.local.setRevision(id, revision, doc.updatedAt);
				uploadedAny = true;
			} catch (err) {
				// The account is at its plan's active-document limit, or its plan
				// can't write this kind at all (a free account's patterns). Every id
				// left here is a create too, so each would be refused the same way:
				// stop rather than spend a PUT on each. The device copies stay, and
				// the next list() — e.g. the re-list after an archive frees a slot,
				// or the first visit after upgrading — tries again.
				if (err instanceof DocLimitError || err instanceof ProRequiredError) break;
				if (err instanceof DocDeletedError) {
					// This id is tombstoned. `ids` here is `toUpload`, which — per
					// resolveLocalOnly — only ever contains ids where getSyncMeta(id)
					// == null: we have NO evidence this doc was ever synced. By the
					// same no-syncMeta-means-dirty rule as resolveTombstone, that is
					// never safe to purge, so just leave it alone; it'll hit this same
					// 410 (one no-op PUT) on every future gallery visit. Trading a
					// wasted request for never destroying possibly-unsynced work is
					// the deliberate call — this path is already rare (a doc created
					// and deleted on another device before this one ever listed it).
					continue;
				}
				if (err instanceof DocConflictError) continue;
				console.debug('SyncedDocStore: background upload failed, will retry next visit', id, err);
			}
		}
		if (uploadedAny) this.notifyBackgroundSync();
	}

	/** Caches remote thumbnails the local store doesn't have yet — the list()
	 *  that triggered this renders without them, so it notifies background-sync
	 *  subscribers once at least one lands, letting the gallery re-list() and
	 *  paint them. The re-list's own backfill pass finds them already cached and
	 *  notifies nothing, so this terminates after a single extra list(). */
	private async backfillThumbnails(remoteMetas: RemoteDocMeta<TMetaExtra>[], thumbById: Map<string, Blob>): Promise<void> {
		// "Already cached?" must be answered from the thumbnail cache, NOT from
		// localMetas: a remote-only doc (on the server, never opened here) has no
		// localMetas row, so checking localMetas would report "not cached" on every
		// list() — re-fetching and re-saving the same thumbnail each time, flipping
		// cachedAny true, and spinning notifyBackgroundSync -> refresh -> list forever.
		let cachedAny = false;
		for (const r of remoteMetas) {
			if (!r.hasThumbnail) continue;
			if (thumbById.has(r.id)) continue;
			try {
				const blob = await this.remote.getThumbnail(r.id);
				if (blob != null) {
					await this.local.saveThumbnail(r.id, blob);
					cachedAny = true;
				}
			} catch (err) {
				console.debug('SyncedDocStore: thumbnail backfill failed, will retry next visit', r.id, err);
			}
		}
		if (cachedAny) this.notifyBackgroundSync();
	}
}
